import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  SearchCheck,
  LayoutDashboard,
  Tag,
  Package,
  ShoppingCart,
  Users,
  FileText,
  Receipt,
  Building2,
  Store,
  Shield,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MapPin,
  AlertCircle,
  Upload,
  Wallet,
  CreditCard,
  BarChart2,
  Landmark,
  CalendarClock,
  Plus,
  Factory,
  ClipboardCheck,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../ui/Button";
import { useAuthStore } from "../../store/authStore";
import { businessesApi } from "../../api/businesses";
import { outletsApi } from "../../api/outlets";
import { subscriptionApi } from "../../api/subscription";
import { ACCOUNTING_FEATURE } from "../../lib/featureFlags";
import type { Business, Outlet } from "../../types/models";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle?: () => void;
  currentBusiness: Business | null;
  currentOutlet: Outlet | null;
  outlets: Outlet[];
  onBusinessChange: (businessId: string) => void;
  onOutletChange: (outletId: string) => void;
  onOutletsLoad: (outlets: Outlet[]) => void;
}

// `permission` gates the nav item against the current user's permissions
// (owner/admin bypass). Items without a permission are always shown (e.g.
// Dashboard). The Organization group is gated separately on org_role.
// `anyPermission` is for routes the server gates with anyOf(...): the item shows if
// the user holds any one of them. Gating such a route on a single permission would
// hide it from people the server lets through.
interface NavItem {
  icon: typeof LayoutDashboard;
  label: string;
  to: string;
  permission?: string;
  anyPermission?: string[];
  /** Paths below `to` that belong to another item. Drafts lives at /sales and
   *  stays lit on an open draft (/sales/:id), but New Sale at /sales/new is its
   *  own item and must not light Drafts up as well. */
  notUnder?: string[];
}

const navGroups: { title: string; items: NavItem[] }[] = [
  {
    title: "Main",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", to: "/dashboard" },
    ],
  },
  {
    title: "Sales",
    items: [
      { icon: Plus, label: "New Sale", to: "/sales/new", permission: "orders:create" },
      { icon: FileText, label: "Drafts", to: "/sales", permission: "orders:view", notUnder: ["/sales/new"] },
    ],
  },
  {
    title: "Menu",
    items: [
      { icon: Tag, label: "Categories", to: "/menu/categories", permission: "categories:view" },
      { icon: Package, label: "Items", to: "/menu/items", permission: "menu:view" },
      { icon: Upload, label: "Import", to: "/menu/import", permission: "data:import" },
    ],
  },
  {
    title: "Inventory",
    items: [
      { icon: ShoppingCart, label: "Stock Levels", to: "/inventory", permission: "stock:view" },
      { icon: FileText, label: "Transactions", to: "/inventory/transactions", permission: "stock:view" },
    ],
  },
  {
    title: "Production",
    items: [
      {
        icon: Factory,
        label: "Production Runs",
        to: "/production",
        permission: "production:view",
        notUnder: ["/production/new", "/production/day-sheet"],
      },
      { icon: Plus, label: "New Run", to: "/production/new", permission: "production:manage" },
      { icon: ClipboardCheck, label: "Day Sheet", to: "/production/day-sheet", permission: "production:view" },
    ],
  },
  {
    title: "Supply Chain",
    items: [
      { icon: Users, label: "Suppliers", to: "/supply-chain/suppliers", permission: "suppliers:view" },
      { icon: ShoppingCart, label: "Purchase Orders", to: "/supply-chain/purchase-orders", permission: "purchase_orders:view" },
      { icon: AlertCircle, label: "Payables", to: "/supply-chain/payables", permission: "purchase_orders:view" },
    ],
  },
  {
    title: "Operations",
    items: [
      { icon: FileText, label: "Bills", to: "/bills", permission: "bills:history" },
      { icon: Receipt, label: "Credit Notes / Returns", to: "/credit-notes", permission: "credit_notes:view" },
      { icon: Users, label: "Customers", to: "/customers", permission: "customers:view" },
      { icon: FileText, label: "Quotations", to: "/quotations", permission: "quotations:view" },
      { icon: FileText, label: "Price Bands", to: "/menu/price-bands", permission: "menu:view" },
    ],
  },
  {
    title: "Reports",
    items: [
      { icon: FileText, label: "The Day", to: "/reports/business-day", permission: "reports:view" },
      { icon: FileText, label: "Sales Summary", to: "/reports/sales", permission: "reports:view" },
      { icon: Package, label: "Item Sales", to: "/reports/items", permission: "reports:view" },
      { icon: BarChart2, label: "Menu Performance (80/20)", to: "/reports/menu-performance", permission: "reports:view" },
      { icon: Package, label: "Stock Flow", to: "/reports/stock-flow", permission: "reports:view" },
      { icon: BarChart2, label: "Margin by Item", to: "/reports/margin", permission: "pricing:view_cost" },
      { icon: BarChart2, label: "Priced Below Suggestion", to: "/reports/pricing-exceptions", permission: "pricing:view_cost" },
      { icon: Package, label: "Stock Value", to: "/reports/stock-value", permission: "pricing:view_cost" },
      { icon: SearchCheck, label: "Cost Check", to: "/reports/cost-check", permission: "pricing:view_cost" },
      { icon: AlertCircle, label: "Receivables Ageing", to: "/reports/receivables-ageing", permission: "receivables:view" },
      { icon: Users, label: "Sales by Customer", to: "/reports/sales-by-customer", permission: "receivables:view" },
      { icon: FileText, label: "GST Reports", to: "/reports/gst", permission: "reports:view" },
      { icon: FileText, label: "Composition Report", to: "/reports/composition", permission: "reports:view" },
      { icon: CreditCard, label: "Payment Reconciliation", to: "/reports/payment-reconciliation", permission: "reports:view" },
      { icon: Users, label: "Staff Performance", to: "/reports/staff-performance", permission: "reports:view" },
      {
        icon: CalendarClock,
        label: "Day Close (EOD)",
        to: "/reports/day-close",
        anyPermission: ["sessions:view", "reports:view"],
      },
      { icon: FileText, label: "Trial Balance", to: "/reports/trial-balance", permission: "financial_reports:view" },
      { icon: FileText, label: "Profit & Loss", to: "/reports/profit-loss", permission: "financial_reports:view" },
      { icon: FileText, label: "Balance Sheet", to: "/reports/balance-sheet", permission: "financial_reports:view" },
    ],
  },
  {
    title: "Accounting",
    items: [
      { icon: Landmark, label: "Opening Balances", to: "/accounting/opening-balances", permission: "accounting:view" },
      { icon: FileText, label: "Chart of Accounts", to: "/accounting/accounts", permission: "accounting:view" },
      { icon: FileText, label: "Journal Entries", to: "/accounting/journals", permission: "accounting:view" },
      { icon: Wallet, label: "Petty Cash Book", to: "/accounting/petty-cash", permission: "accounting:view" },
      { icon: Wallet, label: "Cash Book", to: "/accounting/cash-book", permission: "accounting:view" },
    ],
  },
  {
    title: "Business",
    items: [
      { icon: Store, label: "Outlets", to: "/business/outlets", permission: "outlets:view" },
      { icon: Users, label: "Staff", to: "/business/staff", permission: "staff:view" },
      { icon: Shield, label: "Roles", to: "/business/roles", permission: "roles:view" },
      { icon: Shield, label: "Settings", to: "/business/settings", permission: "settings:view" },
    ],
  },
  {
    title: "Organization",
    items: [
      { icon: Building2, label: "Businesses", to: "/org/businesses" },
      { icon: Users, label: "Members", to: "/org/members" },
      { icon: Shield, label: "Role Templates", to: "/org/role-templates" },
      { icon: Shield, label: "Settings", to: "/org/settings" },
      { icon: CreditCard, label: "Subscription", to: "/org/subscription" },
    ],
  },
];

const getGroupTranslationKey = (title: string) => {
  switch (title) {
    case "Main": return "sidebar.main";
    case "Menu": return "sidebar.menu";
    case "Inventory": return "sidebar.inventory";
    case "Production": return "sidebar.production";
    case "Sales": return "sidebar.sales";
    case "Supply Chain": return "sidebar.supplyChain";
    case "Operations": return "sidebar.operations";
    case "Reports": return "sidebar.reports";
    case "Business": return "sidebar.business";
    case "Organization": return "sidebar.organization";
    default: return title;
  }
};

const getItemTranslationKey = (label: string) => {
  switch (label) {
    case "Dashboard": return "sidebar.dashboard";
    case "New Sale": return "sidebar.newSale";
    case "Drafts": return "sidebar.drafts";
    case "Categories": return "sidebar.categories";
    case "Items": return "sidebar.items";
    case "Import": return "sidebar.import";
    case "Stock Levels": return "sidebar.stockLevels";
    case "Production Runs": return "sidebar.productionRuns";
    case "New Run": return "sidebar.newRun";
    case "Day Sheet": return "sidebar.daySheet";
    case "Transactions": return "sidebar.transactions";
    case "Suppliers": return "sidebar.suppliers";
    case "Purchase Orders": return "sidebar.purchaseOrders";
    case "Payables": return "sidebar.payables";
    case "Bills": return "sidebar.bills";
    case "Customers": return "sidebar.customers";
    case "Tables": return "sidebar.tables";
    case "Sales Summary": return "sidebar.salesSummary";
    case "Item Sales": return "sidebar.itemSales";
    case "Stock Flow": return "sidebar.stockFlow";
    case "Receivables Ageing": return "sidebar.receivablesAgeing";
    case "Sales by Customer": return "sidebar.salesByCustomer";
    case "GST Reports": return "sidebar.gstReports";
    case "Composition Report": return "sidebar.compositionReport";
    case "Payment Reconciliation": return "sidebar.paymentReconciliation";
    case "Day Close (EOD)": return "sidebar.dayClose";
    case "Outlets": return "sidebar.outlets";
    case "Staff": return "sidebar.staff";
    case "Staff Performance": return "sidebar.staffPerformance";
    case "Roles": return "sidebar.roles";
    case "Settings": return "sidebar.settings";
    case "Businesses": return "sidebar.businesses";
    case "Members": return "sidebar.members";
    case "Role Templates": return "sidebar.roleTemplates";
    case "Subscription": return "sidebar.subscription";
    default: return label;
  }
};


export function Sidebar({
  isOpen,
  onClose,
  onToggle,
  currentBusiness,
  currentOutlet,
  outlets,
  onBusinessChange,
  onOutletChange,
  onOutletsLoad,
}: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { allBusinesses, setAllBusinesses, user, permissions } = useAuthStore();
  const [businessMenuOpen, setBusinessMenuOpen] = useState(false);
  const [outletMenuOpen, setOutletMenuOpen] = useState(false);

  const { data: subscription } = useQuery({
    queryKey: ["subscription-status"],
    queryFn: () => subscriptionApi.getStatus(),
    staleTime: 60000,
    enabled: !!user,
  });

  // Absent key = off: the accounting module is half-built.
  const hasAccounting = subscription?.features?.[ACCOUNTING_FEATURE] === true;

  // owner/admin bypass; everyone else is gated by their permission array.
  const orgRole = user?.org_role;
  const can = (permission?: string) => {
    if (!permission) return true;
    if (orgRole === "owner" || orgRole === "admin") return true;
    return permissions.includes(permission);
  };
  // anyOf(...) routes: any one of the listed permissions shows the item.
  const canAny = (perms?: string[]) => {
    if (!perms || perms.length === 0) return true;
    if (orgRole === "owner" || orgRole === "admin") return true;
    return perms.some((p) => permissions.includes(p));
  };

  useEffect(() => {
    if (!allBusinesses) {
      businessesApi.listBusinesses().then(setAllBusinesses).catch(console.error);
    }
  }, [allBusinesses, setAllBusinesses]);

  useEffect(() => {
    if (currentBusiness) {
      outletsApi.listOutlets(currentBusiness.id).then(onOutletsLoad).catch(console.error);
    }
  }, [currentBusiness?.id, onOutletsLoad]);

  const isActive = (to: string, notUnder: string[] = []) => {
    const path = location.pathname;
    if (notUnder.some((p) => path === p || path.startsWith(p + "/"))) return false;
    return path === to || path.startsWith(to + "/");
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden print:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={[
          // print:hidden is not decoration. At print width the lg: breakpoint
          // never applies, so this stays `fixed inset-y-0` — its mobile drawer
          // form — and a fixed element prints over the first page.
          "fixed lg:static inset-y-0 left-0 z-50 bg-sidebar flex flex-col transition-all duration-200 print:hidden",
          isOpen ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0 lg:w-16",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Header */}
        <div className={`h-14 flex items-center border-b border-sidebar-hover transition-all duration-200 ${
          isOpen ? "justify-between px-4" : "justify-center px-2"
        }`}>
          <Link to="/" className="flex items-center font-semibold text-sidebar-text whitespace-nowrap">
            <span className="text-lg font-bold">B</span>
            <span className={`transition-all duration-200 overflow-hidden whitespace-nowrap text-lg ${
              isOpen ? "max-w-xs opacity-100 ml-0.5" : "max-w-0 opacity-0"
            }`}>
              2B ERP
            </span>
          </Link>
          {isOpen && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-sidebar-text hover:bg-sidebar-hover lg:hidden"
              icon={<X className="h-4 w-4" />}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {/* Business Selector — cloud mode only (local mode has a single fixed business/outlet) */}
          <div className="px-3 mb-4">
            <div className="space-y-2">
              {/* Business Selector */}
              <div className="relative">
                <button
                  onClick={() => {
                    if (!isOpen) {
                      if (onToggle) onToggle();
                      setBusinessMenuOpen(true);
                      setOutletMenuOpen(false);
                    } else {
                      setBusinessMenuOpen(!businessMenuOpen);
                      setOutletMenuOpen(false);
                    }
                  }}
                  title={!isOpen ? (currentBusiness?.name || "Select Business") : undefined}
                  className={`w-full flex items-center text-sm text-sidebar-text hover:bg-sidebar-hover rounded-md transition-all duration-150 ${
                    isOpen ? "justify-between px-3 py-2" : "justify-center p-2"
                  }`}
                >
                  <div className="flex items-center">
                    <Building2 className="h-5 w-5 flex-shrink-0" />
                    <span className={`transition-all duration-200 overflow-hidden whitespace-nowrap text-left ${
                      isOpen ? "max-w-xs opacity-100 ml-2" : "max-w-0 opacity-0"
                    }`}>
                      {currentBusiness?.name || "Select Business"}
                    </span>
                  </div>
                  {isOpen && (
                    <ChevronDown
                      className={[
                        "h-4 w-4 transition-transform flex-shrink-0",
                        businessMenuOpen ? "rotate-180" : "",
                      ].filter(Boolean)
                        .join(" ")}
                    />
                  )}
                </button>

                {/* Business dropdown menu */}
                {isOpen && businessMenuOpen && allBusinesses && (
                  <div className="mt-1 ml-8 mr-2 space-y-1">
                    {allBusinesses.map((business) => (
                      <button
                        key={business.id}
                        onClick={() => {
                          onBusinessChange(business.id);
                          setBusinessMenuOpen(false);
                          if (window.innerWidth < 1024) onClose();
                        }}
                        className={[
                          "w-full text-left px-3 py-1.5 text-sm rounded-md",
                          currentBusiness?.id === business.id
                            ? "bg-sidebar-hover text-sidebar-text"
                            : "text-sidebar-text hover:bg-sidebar-hover",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {business.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Outlet Selector */}
              {currentBusiness && (
                <div className="relative">
                  <button
                    onClick={() => {
                      if (!isOpen) {
                        if (onToggle) onToggle();
                        setOutletMenuOpen(true);
                        setBusinessMenuOpen(false);
                      } else {
                        setOutletMenuOpen(!outletMenuOpen);
                        setBusinessMenuOpen(false);
                      }
                    }}
                    title={!isOpen ? (currentOutlet?.name || "All Outlets") : undefined}
                    className={`w-full flex items-center text-sm text-sidebar-text hover:bg-sidebar-hover rounded-md transition-all duration-150 ${
                      isOpen ? "justify-between px-3 py-2" : "justify-center p-2"
                    }`}
                  >
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 flex-shrink-0" />
                      <span className={`transition-all duration-200 overflow-hidden whitespace-nowrap text-left ${
                        isOpen ? "max-w-xs opacity-100 ml-2" : "max-w-0 opacity-0"
                      }`}>
                        {currentOutlet?.name || "All Outlets"}
                      </span>
                    </div>
                    {isOpen && (
                      <ChevronDown
                        className={[
                          "h-4 w-4 transition-transform flex-shrink-0",
                          outletMenuOpen ? "rotate-180" : "",
                        ].filter(Boolean)
                          .join(" ")}
                      />
                    )}
                  </button>

                  {/* Outlet dropdown menu */}
                  {isOpen && outletMenuOpen && (
                    <div className="mt-1 ml-8 mr-2 space-y-1">
                      <button
                        onClick={() => {
                          onOutletChange("");
                          setOutletMenuOpen(false);
                          if (window.innerWidth < 1024) onClose();
                        }}
                        className={[
                          "w-full text-left px-3 py-1.5 text-sm rounded-md",
                          !currentOutlet
                            ? "bg-sidebar-hover text-sidebar-text"
                            : "text-sidebar-text hover:bg-sidebar-hover",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        All Outlets
                      </button>
                      {outlets.map((outlet) => (
                        <button
                          key={outlet.id}
                          onClick={() => {
                            onOutletChange(outlet.id);
                            setOutletMenuOpen(false);
                            if (window.innerWidth < 1024) onClose();
                          }}
                          className={[
                            "w-full text-left px-3 py-1.5 text-sm rounded-md",
                            currentOutlet?.id === outlet.id
                              ? "bg-sidebar-hover text-sidebar-text"
                              : "text-sidebar-text hover:bg-sidebar-hover",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {outlet.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {navGroups.map((group, groupIdx) => {
              const isOrgGroup = group.title === "Organization";
              const isBusinessGroup = group.title === "Business";

              // Hide org section unless user has an org role; hide business section without a selected business
              if (isOrgGroup && !["owner", "admin"].includes(user?.org_role ?? "")) return null;
              if (isBusinessGroup && !currentBusiness) return null;

              // The accounting module is off unless the plan says otherwise.
              if (group.title === "Accounting" && !hasAccounting) return null;

              // Hide nav items the user lacks permission for; drop the whole
              // group when nothing in it is visible.
              const visibleItems = group.items.filter((item) => {
                // Trial Balance / P&L / Balance Sheet are accounting reports living
                // in the Reports group; they follow the same flag as the module.
                if (item.permission === "financial_reports:view" && !hasAccounting) return false;
                return can(item.permission) && canAny(item.anyPermission);
              });
              if (visibleItems.length === 0) return null;

              return (
                <div key={group.title} className="mb-4">
                  {isOpen ? (
                    <div className="transition-all duration-200 overflow-hidden max-h-8 opacity-100 mb-2">
                      <h3 className="px-3 text-xs font-semibold text-sidebar-text uppercase tracking-wider whitespace-nowrap">
                        {t(getGroupTranslationKey(group.title))}
                      </h3>
                    </div>
                  ) : (
                    groupIdx > 0 && <hr className="border-sidebar-hover/30 my-2 mx-3" />
                  )}
                  {visibleItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => {
                        if (window.innerWidth < 1024) onClose();
                      }}
                      title={!isOpen ? t(getItemTranslationKey(item.label)) : undefined}
                      className={[
                        "flex items-center px-3 py-2 text-sm rounded-md mx-2 transition-all duration-150",
                        isOpen ? "justify-start" : "justify-center px-2",
                        isActive(item.to, item.notUnder)
                          ? "bg-sidebar-hover text-sidebar-text"
                          : "text-sidebar-text hover:bg-sidebar-hover",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      <span className={`transition-all duration-200 overflow-hidden whitespace-nowrap text-left ${
                        isOpen ? "max-w-xs opacity-100 ml-3" : "max-w-0 opacity-0"
                      }`}>
                        {t(getItemTranslationKey(item.label))}
                      </span>
                    </Link>
                  ))}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Footer Toggle Button (Desktop only) */}
        {onToggle && (
          <div className="hidden lg:flex p-2 border-t border-sidebar-hover justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="w-full flex items-center justify-center text-sidebar-text hover:bg-sidebar-hover p-2 rounded-md"
              icon={
                isOpen ? (
                  <ChevronLeft className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )
              }
            />
          </div>
        )}
      </aside>
    </>
  );
}
